import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Fab,
    Paper,
    Typography,
    IconButton,
    TextField,
    Avatar,
    Stack,
    CircularProgress,
    Zoom,
    Fade,
    Chip,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Alert,
    Menu,
    MenuItem,
    Checkbox,
    Collapse
} from '@mui/material';
import {
    Close as CloseIcon,
    Send as SendIcon,
    Chat as ChatIcon,
    Delete as DeleteIcon,
    Lightbulb,
    Event as EventIcon,
    Dashboard as DashboardIcon,
    History as HistoryIcon,
    Add as AddIcon,
    Mic as MicIcon,
    OpenInFull as OpenInFullIcon,
    CloseFullscreen as CloseFullscreenIcon,
    Task as TaskIcon,
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as TodoIcon,
    Pending as InProgressIcon,
    Block as BlockedIcon,
    FlagOutlined as PriorityIcon,
    Comment as CommentIcon,
    ViewKanban as KanbanIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    PlaylistAddCheck as SubtaskIcon
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import LynaAvatar from './LynaAvatar';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const quickQuestions = [
    { icon: <Lightbulb fontSize="small" />, label: 'Optimizar agenda', query: '¿Cómo puedo optimizar mejor mi agenda de turnos?' },
    { icon: <EventIcon fontSize="small" />, label: 'Crear reserva', query: 'Quiero crear una reserva' },
    { icon: <TaskIcon fontSize="small" />, label: 'Mis tareas', query: 'Ver mis tareas pendientes' },
    { icon: <KanbanIcon fontSize="small" />, label: 'Mis proyectos', query: 'Ver mis proyectos' }
];

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
    urgent: { label: 'Urgente', color: '#ef4444' },
    high:   { label: 'Alta',    color: '#f97316' },
    medium: { label: 'Media',   color: '#eab308' },
    low:    { label: 'Baja',    color: '#22c55e' }
};

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    todo:        { label: 'Pendiente',   color: '#6b7280', icon: <TodoIcon fontSize="inherit" /> },
    in_progress: { label: 'En progreso', color: '#3b82f6', icon: <InProgressIcon fontSize="inherit" /> },
    blocked:     { label: 'Bloqueada',   color: '#ef4444', icon: <BlockedIcon fontSize="inherit" /> },
    done:        { label: 'Completada',  color: '#22c55e', icon: <CheckCircleIcon fontSize="inherit" /> }
};

interface LynaChatProps {
    isOpen?: boolean;
    onClose?: () => void;
}

interface Message {
    role: 'user' | 'assistant';
    content: string;
    suggestions?: string[];
}

interface Conversation {
    conversationId: string;
    title?: string;
    lastMessageAt: string;
    messageCount: number;
}

const LynaChat: React.FC<LynaChatProps> = ({ isOpen: externalIsOpen, onClose }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const { user } = useAuth();
    
    const displayIsOpen = externalIsOpen !== undefined ? externalIsOpen : isOpen;
    const [conversationId, setConversationId] = useState<string>('');
    const [messages, setMessages] = useState<Message[]>([
        { 
            role: 'assistant', 
            content: '¡Hola! 🐾 Soy Lyna, tu asistente inteligente de Turn.ar.\n\nPuedo ayudarte con:\n• Optimización de agendas y horarios\n• Consejos para tu negocio\n• Gestión de clientes\n• Crear reservas rápidamente\n• Y mucho más...\n\n¿En qué puedo ayudarte hoy?',
            suggestions: ['¿Cómo empezar?', 'Crear una reserva', 'Tips para mi negocio']
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [showMentionMenu, setShowMentionMenu] = useState(false);
    const [mentionResults, setMentionResults] = useState<any[]>([]);
    const [isSearchingClient, setIsSearchingClient] = useState(false);
    const [showBoardMenu, setShowBoardMenu] = useState(false);
    const [boardResults, setBoardResults] = useState<any[]>([]);
    const [isSearchingBoard, setIsSearchingBoard] = useState(false);
    const [showTaskMenu, setShowTaskMenu] = useState(false);
    const [taskResults, setTaskResults] = useState<any[]>([]);
    const [isSearchingTask, setIsSearchingTask] = useState(false);
    const [commentDialog, setCommentDialog] = useState<{ open: boolean; taskId: number | null; taskTitle: string }>({ open: false, taskId: null, taskTitle: '' });
    const [commentText, setCommentText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [showStats, setShowStats] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [stats, setStats] = useState<any>(null);
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const [createReservationDialog, setCreateReservationDialog] = useState<{ open: boolean; data: any }>({ open: false, data: null });
    const scrollRef = useRef<HTMLDivElement>(null);
    const { mode } = useThemeMode();
    const isDark = mode === 'dark';

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    useEffect(() => {
        if (displayIsOpen && conversations.length === 0) {
            fetchConversations();
        }
    }, [displayIsOpen]);

    const fetchConversations = async () => {
        try {
            const response = await axios.get('/api/chat/conversations');
            setConversations(response.data);
        } catch (error) {
            console.error('Error fetching conversations:', error);
        }
    };

    const handleDeleteConversation = async (e: React.MouseEvent, convId: string) => {
        e.stopPropagation();
        try {
            await axios.delete(`/api/chat/conversations/${convId}`);
            setConversations(prev => prev.filter(c => c.conversationId !== convId));
            if (conversationId === convId) {
                startNewConversation();
            }
        } catch (err) {
            console.error('Error deleting conversation:', err);
        }
    };

    // Escucha de Voz
    const handleVoiceRecord = () => {
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) {
            alert('Lamentablemente, tu navegador no soporta el dictado por voz.');
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = 'es-ES';
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => setIsListening(true);
        recognition.onresult = (event: any) => {
            const transcript = Array.from(event.results)
                .map((result: any) => result[0].transcript)
                .join('');
            setInputValue(transcript);
        };
        recognition.onerror = (error: any) => {
            console.error('Error en el microfono:', error);
            setIsListening(false);
        };
        recognition.onend = () => setIsListening(false);

        recognition.start();
    };

    const parseSuggestions = (s: any): string[] => {
        if (Array.isArray(s)) return s;
        if (typeof s === 'string') {
            try {
                const parsed = JSON.parse(s);
                if (Array.isArray(parsed)) return parsed;
            } catch {
                return [];
            }
        }
        return [];
    };

    const fetchMessages = async (convId: string) => {
        try {
            const response = await axios.get(`/api/chat/conversations/${convId}?limit=50`);
            const msgs = response.data.map((m: any) => ({
                role: m.role,
                content: m.content,
                suggestions: parseSuggestions(m.suggestions)
            }));
            setMessages(msgs.length > 0 ? msgs : [{
                role: 'assistant',
                content: '¡Hola! 🐾 Soy Lyna. ¿En qué puedo ayudarte?',
                suggestions: ['¿Cómo empezar?', 'Crear una reserva']
            }]);
            setConversationId(convId);
            setShowHistory(false);
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
    };

    const startNewConversation = () => {
        setConversationId('');
        setMessages([{ 
            role: 'assistant', 
            content: '¡Hola! 🐾 Soy Lyna. ¿En qué puedo ayudarte?',
            suggestions: ['¿Cómo empezar?', 'Crear una reserva']
        }]);
        setShowHistory(false);
    };

    const loadStats = async () => {
        if (stats) {
            setShowStats(!showStats);
            return;
        }
        
        setIsLoadingStats(true);
        try {
            const response = await axios.get('/api/ai/stats');
            setStats(response.data);
            setShowStats(true);
        } catch (error) {
            console.error('Error loading stats:', error);
        } finally {
            setIsLoadingStats(false);
        }
    };

    const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setInputValue(val);

        const lastWord = val.split(' ').pop();

        // @ = buscar clientes
        if (lastWord && lastWord.startsWith('@')) {
            const query = lastWord.substring(1);
            setShowMentionMenu(true);
            setShowBoardMenu(false);
            setShowTaskMenu(false);
            setIsSearchingClient(true);
            try {
                const response = await axios.post('/api/ai/search', { type: 'clients', query: query || 'a' });
                setMentionResults(response.data.results || []);
            } catch (err) {
                console.error(err);
            } finally {
                setIsSearchingClient(false);
            }
        // # = buscar agendas
        } else if (lastWord && lastWord.startsWith('#')) {
            const query = lastWord.substring(1);
            setShowBoardMenu(true);
            setShowMentionMenu(false);
            setShowTaskMenu(false);
            setIsSearchingBoard(true);
            try {
                const response = await axios.post('/api/ai/search', { type: 'boards', query: query || 'a' });
                setBoardResults(response.data.results || []);
            } catch (err) {
                console.error(err);
            } finally {
                setIsSearchingBoard(false);
            }
        // % = buscar tareas
        } else if (lastWord && lastWord.startsWith('%')) {
            const query = lastWord.substring(1);
            setShowTaskMenu(true);
            setShowMentionMenu(false);
            setShowBoardMenu(false);
            setIsSearchingTask(true);
            try {
                const response = await axios.post('/api/ai/search', { type: 'tasks', query: query || 'a' });
                setTaskResults(response.data.results || []);
            } catch (err) {
                console.error(err);
            } finally {
                setIsSearchingTask(false);
            }
        } else {
            setShowMentionMenu(false);
            setShowBoardMenu(false);
            setShowTaskMenu(false);
        }
    };

    const selectMention = (clientName: string) => {
        const words = inputValue.split(' ');
        words.pop();
        const newValue = words.join(' ') + (words.length > 0 ? ' ' : '') + `${clientName} `;
        setInputValue(newValue);
        setShowMentionMenu(false);
    };

    const selectBoardMention = (boardName: string) => {
        const words = inputValue.split(' ');
        words.pop();
        const newValue = words.join(' ') + (words.length > 0 ? ' ' : '') + `#${boardName} `;
        setInputValue(newValue);
        setShowBoardMenu(false);
    };

    const selectTaskMention = (taskTitle: string) => {
        const words = inputValue.split(' ');
        words.pop();
        const newValue = words.join(' ') + (words.length > 0 ? ' ' : '') + `"${taskTitle}" `;
        setInputValue(newValue);
        setShowTaskMenu(false);
    };

    const handleSend = async (content?: string) => {
        const messageText = content || inputValue;
        if (!messageText.trim()) return;

        const newMessages: Message[] = [...messages, { role: 'user', content: messageText }];
        setMessages(newMessages);
        setInputValue('');
        setIsLoading(true);

        try {
            const response = await axios.post('/api/ai/chat', { 
                messages: newMessages.slice(-10),
                options: { conversationId }
            });
            
            const assistantMessage: Message = { 
                role: 'assistant', 
                content: response.data.response || 'Ups, algo salió mal.',
                suggestions: response.data.suggestions || []
            };
            
            setMessages([...newMessages, assistantMessage]);
            
            if (response.data.aiCommands && response.data.aiCommands.length > 0) {
                for (const cmd of response.data.aiCommands) {
                    try {
                        if (cmd.type === 'CREATE_RESERVATION') {
                            await handleCreateReservation(cmd.data);
                        } else if (cmd.type === 'CANCEL_RESERVATION') {
                            await axios.delete(`/api/reservations/${cmd.data.id}`);
                            setMessages(prev => [...prev, { role: 'assistant', content: `✅ ¡Reserva cancelada exitosamente!`, suggestions: ['Ver agenda'] }]);
                        } else if (cmd.type === 'UPDATE_RESERVATION') {
                            await axios.put(`/api/reservations/${cmd.data.id}`, { startDate: cmd.data.newStartDate, endDate: new Date(new Date(cmd.data.newStartDate).getTime() + 60*60*1000) });
                            setMessages(prev => [...prev, { role: 'assistant', content: `✅ ¡Reserva reprogramada exitosamente!`, suggestions: ['Ver agenda'] }]);
                        } else if (cmd.type === 'CREATE_PROJECT') {
                            const r = await axios.post(`/api/ai/create-project`, cmd.data);
                            setMessages(prev => [...prev, { role: 'assistant', content: `📁 ¡Proyecto "${r.data.project.name}" creado exitosamente!`, suggestions: ['Crear tarea', 'Ver proyectos'] }]);
                        } else if (cmd.type === 'CREATE_TASK') {
                            const r = await axios.post(`/api/ai/create-task`, cmd.data);
                            const pn = r.data.projectName ? ` en "${r.data.projectName}"` : '';
                            setMessages(prev => [...prev, { role: 'assistant', content: `✅ ¡Tarea "${cmd.data.title}" creada${pn}!`, suggestions: ['Ver tareas', 'Crear otra tarea'] }]);
                        } else if (cmd.type === 'COMPLETE_TASK') {
                            await axios.put(`/api/tasks/${cmd.data.id}`, { status: 'done' });
                            setMessages(prev => [...prev, { role: 'assistant', content: `✅ ¡Tarea marcada como completada!`, suggestions: ['Ver tareas'] }]);
                        } else if (cmd.type === 'UPDATE_TASK_STATUS') {
                            await axios.put(`/api/tasks/${cmd.data.id}`, { status: cmd.data.status });
                            const statusLabel = STATUS_CONFIG[cmd.data.status]?.label || cmd.data.status;
                            setMessages(prev => [...prev, { role: 'assistant', content: `✅ ¡Tarea movida a "${statusLabel}"!`, suggestions: ['Ver tareas'] }]);
                        } else if (cmd.type === 'UPDATE_TASK_PRIORITY') {
                            await axios.put(`/api/ai/tasks/${cmd.data.id}/priority`, { priority: cmd.data.priority });
                            const priorityLabel = PRIORITY_CONFIG[cmd.data.priority]?.label || cmd.data.priority;
                            setMessages(prev => [...prev, { role: 'assistant', content: `⚡ Prioridad actualizada a "${priorityLabel}"!`, suggestions: ['Ver tareas'] }]);
                        } else if (cmd.type === 'ADD_SUBTASK') {
                            await axios.post(`/api/ai/tasks/${cmd.data.taskId}/subtasks`, { title: cmd.data.title });
                            setMessages(prev => [...prev, { role: 'assistant', content: `📋 Subtarea "${cmd.data.title}" agregada exitosamente!`, suggestions: ['Ver tareas'] }]);
                        } else if (cmd.type === 'TOGGLE_SUBTASK') {
                            await axios.put(`/api/ai/subtasks/${cmd.data.subtaskId}/toggle`, { isCompleted: cmd.data.isCompleted });
                            setMessages(prev => [...prev, { role: 'assistant', content: `☑️ Subtarea actualizada!`, suggestions: ['Ver tareas'] }]);
                        } else if (cmd.type === 'ADD_TASK_COMMENT') {
                            await axios.post(`/api/ai/tasks/${cmd.data.id}/comment`, { content: cmd.data.comment });
                            setMessages(prev => [...prev, { role: 'assistant', content: `💬 ¡Comentario agregado a la tarea!`, suggestions: ['Ver tareas'] }]);
                        } else if (cmd.type === 'RESCHEDULE_TASK') {
                            await axios.put(`/api/ai/tasks/${cmd.data.id}/reschedule`, { dueDate: cmd.data.dueDate });
                            setMessages(prev => [...prev, { role: 'assistant', content: `📅 ¡Tarea reprogramada exitosamente!`, suggestions: ['Ver tareas'] }]);
                        } else if (cmd.type === 'DELETE_TASK') {
                            await axios.delete(`/api/ai/tasks/${cmd.data.id}`);
                            setMessages(prev => [...prev, { role: 'assistant', content: `🗑️ ¡Tarea eliminada!`, suggestions: ['Ver tareas'] }]);
                        }
                        window.dispatchEvent(new CustomEvent('taskDataChanged'));
                    } catch (cmdErr) {
                        console.error('Error executing AI command:', cmdErr);
                        setMessages(prev => [...prev, { role: 'assistant', content: `❌ Ups, hubo un problema al ejecutar la acción: ${cmd.type}` }]);
                    }
                }
            }
            
            if (response.data.conversationId && !conversationId) {
                setConversationId(response.data.conversationId);
            }
            
            fetchConversations();
        } catch (error) {
            console.error('Chat error:', error);
            setMessages([...newMessages, { 
                role: 'assistant', 
                content: 'Lo siento, tengo problemas de conexión. ¿Podrías intentar de nuevo? 🐱'
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateReservation = async (reservationData: any) => {
        setIsLoading(true);
        try {
            const response = await axios.post('/api/ai/create-reservation', reservationData);
            
            const successMessage: Message = {
                role: 'assistant',
                content: `✅ ¡Reserva creada!\n\n${response.data.message}\n\n¿Necesitas algo más?`,
                suggestions: ['Ver reservas de hoy', 'Crear otra reserva']
            };
            
            setMessages(prev => [...prev, successMessage]);
            setCreateReservationDialog({ open: false, data: null });
            fetchConversations();
        } catch (error: any) {
            const errorMessage: Message = {
                role: 'assistant',
                content: `❌ Error: ${error.response?.data?.error || 'Error desconocido'}`
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const clearChat = async () => {
        try {
            if (conversationId) {
                await axios.delete(`/api/chat/conversations/${conversationId}`);
            }
            startNewConversation();
            fetchConversations();
        } catch (error) {
            console.error('Error clearing chat:', error);
        }
    };

    const handleAddComment = async () => {
        if (!commentDialog.taskId || !commentText.trim()) return;
        try {
            await axios.post(`/api/ai/tasks/${commentDialog.taskId}/comment`, { content: commentText });
            setMessages(prev => [...prev, { role: 'assistant', content: `💬 Comentario agregado a "${commentDialog.taskTitle}":\n"${commentText}"`, suggestions: ['Ver tareas'] }]);
            setCommentDialog({ open: false, taskId: null, taskTitle: '' });
            setCommentText('');
        } catch (e) {
            console.error('Error adding comment:', e);
        }
    };

    const handleCompleteTask = async (taskId: number, taskTitle: string) => {
        try {
            await axios.put(`/api/tasks/${taskId}`, { status: 'done' });
            setMessages(prev => [...prev, { role: 'assistant', content: `✅ ¡"${taskTitle}" marcada como completada!`, suggestions: ['Ver tareas', 'Crear tarea'] }]);
        } catch (e) {
            console.error('Error completing task:', e);
        }
    };

    const TaskCard = ({ task }: { task: any }) => {
        const [currentStatus, setCurrentStatus] = useState<string>(task.status || 'todo');
        const [currentPriority, setCurrentPriority] = useState<string>(task.priority || 'medium');
        const [statusAnchor, setStatusAnchor] = useState<null | HTMLElement>(null);
        const [priorityAnchor, setPriorityAnchor] = useState<null | HTMLElement>(null);
        const [showSubtasks, setShowSubtasks] = useState(false);
        const [subtasks, setSubtasks] = useState<any[]>(task.subtasks || []);
        const [isLoadingSubtasks, setIsLoadingSubtasks] = useState(false);
        const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
        const [isSubmittingSubtask, setIsSubmittingSubtask] = useState(false);
        const [isDeleted, setIsDeleted] = useState(false);

        const priority = PRIORITY_CONFIG[currentPriority] || PRIORITY_CONFIG.medium;
        const status = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.todo;
        const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && currentStatus !== 'done';

        const fetchSubtasks = async () => {
            if (subtasks.length > 0) {
                setShowSubtasks(!showSubtasks);
                return;
            }
            setIsLoadingSubtasks(true);
            try {
                const res = await axios.get(`/api/tasks/${task.id}/subtasks`);
                setSubtasks(res.data || []);
                setShowSubtasks(true);
            } catch (err) {
                console.error('Error fetching subtasks:', err);
            } finally {
                setIsLoadingSubtasks(false);
            }
        };

        const handleStatusSelect = async (newStatus: string) => {
            setStatusAnchor(null);
            if (newStatus === currentStatus) return;
            try {
                await axios.put(`/api/tasks/${task.id}`, { status: newStatus });
                setCurrentStatus(newStatus);
                window.dispatchEvent(new CustomEvent('taskDataChanged'));
            } catch (err) {
                console.error('Error updating task status:', err);
            }
        };

        const handlePrioritySelect = async (newPriority: string) => {
            setPriorityAnchor(null);
            if (newPriority === currentPriority) return;
            try {
                await axios.put(`/api/ai/tasks/${task.id}/priority`, { priority: newPriority });
                setCurrentPriority(newPriority);
                window.dispatchEvent(new CustomEvent('taskDataChanged'));
            } catch (err) {
                console.error('Error updating task priority:', err);
            }
        };

        const handleToggleSubtask = async (subtaskId: number, isCompleted: boolean) => {
            try {
                await axios.put(`/api/ai/subtasks/${subtaskId}/toggle`, { isCompleted: !isCompleted });
                setSubtasks(prev => prev.map(s => s.id === subtaskId ? { ...s, isCompleted: !isCompleted } : s));
                window.dispatchEvent(new CustomEvent('taskDataChanged'));
            } catch (err) {
                console.error('Error toggling subtask:', err);
            }
        };

        const handleAddSubtask = async (e: React.FormEvent) => {
            e.preventDefault();
            if (!newSubtaskTitle.trim()) return;
            setIsSubmittingSubtask(true);
            try {
                const res = await axios.post(`/api/ai/tasks/${task.id}/subtasks`, { title: newSubtaskTitle });
                if (res.data?.subtask) {
                    setSubtasks(prev => [...prev, res.data.subtask]);
                }
                setNewSubtaskTitle('');
                setShowSubtasks(true);
                window.dispatchEvent(new CustomEvent('taskDataChanged'));
            } catch (err) {
                console.error('Error adding subtask:', err);
            } finally {
                setIsSubmittingSubtask(false);
            }
        };

        const handleDeleteTask = async () => {
            try {
                await axios.delete(`/api/ai/tasks/${task.id}`);
                setIsDeleted(true);
                window.dispatchEvent(new CustomEvent('taskDataChanged'));
            } catch (err) {
                console.error('Error deleting task:', err);
            }
        };

        if (isDeleted) {
            return (
                <Alert severity="warning" sx={{ mb: 1, py: 0.5, fontSize: '0.75rem' }}>
                    Tarea "{task.title}" eliminada.
                </Alert>
            );
        }

        const completedSubtasksCount = subtasks.filter(s => s.isCompleted).length;

        return (
            <Box sx={{
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                borderLeft: `4px solid ${priority.color}`,
                borderRadius: 2.5,
                p: 1.5,
                mb: 1.2,
                bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(8px)',
                boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.04)',
                transition: 'all 0.2s',
                '&:hover': { borderLeftWidth: '6px', transform: 'translateY(-1px)' }
            }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.88rem', flex: 1, lineHeight: 1.3, color: currentStatus === 'done' ? 'text.secondary' : 'text.primary', textDecoration: currentStatus === 'done' ? 'line-through' : 'none' }}>
                        {task.title}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexShrink={0} alignItems="center">
                        <Chip
                            size="small"
                            label={priority.label}
                            onClick={(e) => setPriorityAnchor(e.currentTarget)}
                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${priority.color}22`, color: priority.color, border: `1px solid ${priority.color}55`, cursor: 'pointer', '&:hover': { bgcolor: `${priority.color}33` } }}
                        />
                        <IconButton size="small" onClick={handleDeleteTask} sx={{ p: 0.3, opacity: 0.5, '&:hover': { opacity: 1, color: '#ef4444' } }} title="Eliminar tarea">
                            <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                    </Stack>
                </Stack>

                {/* Priority Selector Menu */}
                <Menu anchorEl={priorityAnchor} open={Boolean(priorityAnchor)} onClose={() => setPriorityAnchor(null)}>
                    {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                        <MenuItem key={key} onClick={() => handlePrioritySelect(key)} selected={key === currentPriority} sx={{ fontSize: '0.8rem', py: 0.5, color: cfg.color, fontWeight: 600 }}>
                            ● {cfg.label}
                        </MenuItem>
                    ))}
                </Menu>

                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip
                        icon={<Box sx={{ fontSize: '0.75rem', display: 'flex' }}>{status.icon}</Box>}
                        label={status.label}
                        size="small"
                        onClick={(e) => setStatusAnchor(e.currentTarget)}
                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: `${status.color}22`, color: status.color, border: `1px solid ${status.color}55`, cursor: 'pointer', '&:hover': { bgcolor: `${status.color}33` } }}
                    />

                    {/* Status Selector Menu */}
                    <Menu anchorEl={statusAnchor} open={Boolean(statusAnchor)} onClose={() => setStatusAnchor(null)}>
                        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                            <MenuItem key={key} onClick={() => handleStatusSelect(key)} selected={key === currentStatus} sx={{ fontSize: '0.8rem', py: 0.5 }}>
                                <Box sx={{ mr: 1, color: cfg.color, display: 'flex' }}>{cfg.icon}</Box>
                                {cfg.label}
                            </MenuItem>
                        ))}
                    </Menu>

                    {task.dueDate && (
                        <Chip
                            size="small"
                            label={`${isOverdue ? '⚠️ ' : '📅 '}${new Date(task.dueDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}`}
                            sx={{ height: 20, fontSize: '0.68rem', bgcolor: isOverdue ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.06)', color: isOverdue ? '#ef4444' : 'text.secondary', border: `1px solid ${isOverdue ? '#ef444444' : 'transparent'}` }}
                        />
                    )}

                    {task.assignee && (
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                            👤 {task.assignee}
                        </Typography>
                    )}
                </Stack>

                {/* Task Quick Actions Row */}
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between" sx={{ mt: 1, pt: 0.8, borderTop: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.05)' }}>
                    <Stack direction="row" spacing={0.5}>
                        {currentStatus !== 'done' && (
                            <Button size="small" variant="outlined" sx={{ fontSize: '0.68rem', py: 0.2, px: 1, minWidth: 0, borderRadius: 1.5, borderColor: '#22c55e', color: '#22c55e', '&:hover': { bgcolor: '#22c55e22' } }}
                                onClick={() => handleStatusSelect('done')}>
                                ✓ Completar
                            </Button>
                        )}
                        <Button size="small" variant="outlined" sx={{ fontSize: '0.68rem', py: 0.2, px: 1, minWidth: 0, borderRadius: 1.5, borderColor: '#6366f1', color: '#6366f1', '&:hover': { bgcolor: '#6366f122' } }}
                            onClick={() => setCommentDialog({ open: true, taskId: task.id, taskTitle: task.title })}>
                            💬 Comentar
                        </Button>
                    </Stack>

                    <Button size="small" onClick={fetchSubtasks} sx={{ fontSize: '0.68rem', py: 0.2, px: 0.8, color: 'text.secondary' }} endIcon={showSubtasks ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}>
                        {isLoadingSubtasks ? <CircularProgress size={12} /> : `Subtareas (${completedSubtasksCount}/${subtasks.length})`}
                    </Button>
                </Stack>

                {/* Expandable Subtasks Section */}
                <Collapse in={showSubtasks} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 1, pt: 1, borderTop: isDark ? '1px dashed rgba(255,255,255,0.1)' : '1px dashed rgba(0,0,0,0.1)' }}>
                        {subtasks.map((st) => (
                            <Stack key={st.id} direction="row" alignItems="center" spacing={0.5} sx={{ py: 0.2 }}>
                                <Checkbox
                                    size="small"
                                    checked={st.isCompleted}
                                    onChange={() => handleToggleSubtask(st.id, st.isCompleted)}
                                    sx={{ p: 0.3, color: '#6366f1', '&.Mui-checked': { color: '#22c55e' } }}
                                />
                                <Typography variant="caption" sx={{ fontSize: '0.78rem', textDecoration: st.isCompleted ? 'line-through' : 'none', color: st.isCompleted ? 'text.secondary' : 'text.primary', flex: 1 }}>
                                    {st.title}
                                </Typography>
                            </Stack>
                        ))}

                        {/* Inline Form to Add Subtask */}
                        <Box component="form" onSubmit={handleAddSubtask} sx={{ display: 'flex', gap: 0.5, mt: 0.8 }}>
                            <TextField
                                size="small"
                                placeholder="Nueva subtarea..."
                                value={newSubtaskTitle}
                                onChange={(e) => setNewSubtaskTitle(e.target.value)}
                                fullWidth
                                sx={{ '& .MuiOutlinedInput-root': { height: 28, fontSize: '0.75rem', borderRadius: 1.5 } }}
                            />
                            <IconButton type="submit" size="small" disabled={!newSubtaskTitle.trim() || isSubmittingSubtask} sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, p: 0.5 }}>
                                <AddIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                        </Box>
                    </Box>
                </Collapse>
            </Box>
        );
    };

    const formatTime = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
        
        if (diffHours < 1) return 'Ahora';
        if (diffHours < 24) return `Hace ${diffHours}h`;
        if (diffHours < 48) return 'Ayer';
        return format(date, 'dd MMM', { locale: es });
    };

    return (
        <>
            {externalIsOpen === undefined && (
                <Zoom in={!isOpen}>
                    <Fab 
                        color="primary" 
                        onClick={() => setIsOpen(true)}
                        sx={{ 
                            position: 'fixed', 
                            bottom: 32, 
                            right: 32, 
                            boxShadow: '0 8px 32px rgba(99, 102, 241, 0.4)',
                            zIndex: 1000
                        }}
                    >
                        <ChatIcon />
                    </Fab>
                </Zoom>
            )}

            {/* Expanded Dialog Mode */}
            <Dialog
                open={displayIsOpen && isExpanded}
                onClose={() => setIsExpanded(false)}
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: {
                        width: '90vw',
                        height: '90vh',
                        maxWidth: '1200px',
                        maxHeight: '900px',
                        borderRadius: 4,
                        overflow: 'hidden',
                        bgcolor: isDark ? 'rgba(14, 14, 24, 0.99)' : 'rgba(248, 248, 255, 0.99)',
                        backdropFilter: 'blur(30px)',
                        boxShadow: '0 30px 100px rgba(99,102,241,0.3), 0 0 0 1px rgba(99,102,241,0.15)',
                        display: 'flex',
                        flexDirection: 'column',
                    }
                }}
                sx={{ zIndex: 1400 }}
            >
                {/* Expanded Header */}
                <Box sx={{ p: 2.5, background: 'linear-gradient(135deg, #6366f1 0%, #9B59B6 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ width: 52, height: 52, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(10px)', border: '2px solid rgba(255,255,255,0.3)' }}>
                            <LynaAvatar size={44} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', letterSpacing: '-0.3px' }}>Lyna — Asistente Inteligente</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.8rem' }}>Turn.ar AI · Modo Expandido</Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                        <IconButton color="inherit" size="small" onClick={() => setShowHistory(!showHistory)} title="Historial">
                            <HistoryIcon />
                        </IconButton>
                        <IconButton color="inherit" size="small" onClick={loadStats} title="Ver resumen" disabled={isLoadingStats}>
                            {isLoadingStats ? <CircularProgress size={20} color="inherit" /> : <DashboardIcon />}
                        </IconButton>
                        <IconButton color="inherit" size="small" onClick={clearChat} title="Limpiar">
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                        <IconButton color="inherit" size="small" onClick={() => setIsExpanded(false)} title="Minimizar" sx={{ bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                            <CloseFullscreenIcon fontSize="small" />
                        </IconButton>
                        <IconButton color="inherit" size="small" onClick={() => { setIsExpanded(false); if (onClose) onClose(); else setIsOpen(false); }} sx={{ bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Box>

                {/* Expanded Layout: sidebar + chat */}
                <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                    {/* Left sidebar: history */}
                    <Box sx={{ width: 260, flexShrink: 0, borderRight: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)', overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>Conversaciones</Typography>
                            <IconButton size="small" onClick={startNewConversation}>
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        {conversations.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4, opacity: 0.4 }}>
                                <HistoryIcon sx={{ fontSize: 40, mb: 1 }} />
                                <Typography variant="body2">Sin conversaciones</Typography>
                            </Box>
                        ) : (
                            <List dense sx={{ p: 0 }}>
                                {conversations.map((conv) => (
                                    <ListItemButton
                                        key={conv.conversationId}
                                        onClick={() => fetchMessages(conv.conversationId)}
                                        sx={{ borderRadius: 2, mb: 0.5, bgcolor: conv.conversationId === conversationId ? 'rgba(99,102,241,0.15)' : 'transparent' }}
                                    >
                                        <ListItemText
                                            primary={`${conv.messageCount} mensajes`}
                                            secondary={formatTime(conv.lastMessageAt)}
                                            primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 500 }}
                                            secondaryTypographyProps={{ fontSize: '0.68rem' }}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                    </Box>

                    {/* Main chat area */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Box
                            ref={scrollRef}
                            sx={{ flex: 1, p: 3, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5,
                                '&::-webkit-scrollbar': { width: '5px' },
                                '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(99,102,241,0.2)', borderRadius: '10px' }
                            }}
                        >
                            {messages.map((m, i) => (
                                <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Box sx={{
                                        maxWidth: '70%',
                                        alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                                        p: 2,
                                        borderRadius: m.role === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                                        bgcolor: m.role === 'user' ? 'primary.main' : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
                                        color: m.role === 'user' ? 'white' : 'inherit',
                                        boxShadow: m.role === 'user' ? '0 4px 12px rgba(99,102,241,0.25)' : 'none'
                                    }}>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '0.95rem' }}>{m.content}</Typography>
                                    </Box>
                                    {(() => {
                                        const suggs = parseSuggestions(m.suggestions);
                                        if (suggs.length === 0 || m.role !== 'assistant') return null;
                                        return (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7, mt: 0.5, ml: 1 }}>
                                                {suggs.map((s, idx) => (
                                                    <Chip key={idx} label={s} size="small" onClick={() => handleSend(s)}
                                                        sx={{ bgcolor: isDark ? 'rgba(149,117,205,0.18)' : 'rgba(99,102,241,0.08)', color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: isDark ? 'rgba(149,117,205,0.3)' : 'rgba(99,102,241,0.15)' } }}
                                                    />
                                                ))}
                                            </Box>
                                        );
                                    })()}
                                </Box>
                            ))}
                            {isLoading && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                                    <CircularProgress size={18} />
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Lyna está escribiendo...</Typography>
                                </Box>
                            )}
                        </Box>

                        {messages.length === 1 && (
                            <Box sx={{ px: 3, pb: 1 }}>
                                <Typography variant="caption" sx={{ opacity: 0.5, mb: 1, display: 'block', fontSize: '0.72rem' }}>Preguntas rápidas:</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {quickQuestions.map((q, idx) => (
                                        <Chip key={idx} icon={q.icon} label={q.label} size="small" onClick={() => handleSend(q.query)}
                                            sx={{ bgcolor: isDark ? 'rgba(149,117,205,0.15)' : 'rgba(99,102,241,0.08)', color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: isDark ? 'rgba(149,117,205,0.3)' : 'rgba(99,102,241,0.15)' } }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Input area expanded */}
                        <Box sx={{ p: 2.5, borderTop: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.06)', flexShrink: 0, position: 'relative' }}>
                            {/* Board Mention Popover (#) - expanded */}
                            {showBoardMenu && (
                                <Paper elevation={8} sx={{ position: 'absolute', bottom: 'calc(100% + 4px)', left: 20, right: 20, maxHeight: 260, overflowY: 'auto', zIndex: 1500, borderRadius: 2, border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)', bgcolor: isDark ? '#1E1E2D' : '#ffffff', boxShadow: '0 -8px 32px rgba(0,0,0,0.2)' }}>
                                    <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'secondary.main', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}># Agendas</Typography>
                                    </Box>
                                    {isSearchingBoard && boardResults.length === 0 ? (
                                        <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
                                    ) : boardResults.length > 0 ? (
                                        <List dense sx={{ p: 0 }}>
                                            {boardResults.map((board, idx) => (
                                                <ListItemButton key={idx} onClick={() => selectBoardMention(board.name)} sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.03)', py: 1.2 }}>
                                                    <Avatar sx={{ width: 32, height: 32, mr: 1.5, fontSize: '0.9rem', bgcolor: 'secondary.main', fontWeight: 700 }}>#</Avatar>
                                                    <ListItemText
                                                        primary={board.name}
                                                        secondary={`${board.type?.replace('_', ' ')} · cap. ${board.capacity ?? '—'}`}
                                                        primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
                                                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                                    />
                                                </ListItemButton>
                                            ))}
                                        </List>
                                    ) : (
                                        <Box sx={{ p: 2, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No se encontraron agendas</Typography></Box>
                                    )}
                                </Paper>
                            )}
                            {/* Client Mention Popover (@) - expanded */}
                            {showMentionMenu && (
                                <Paper elevation={8} sx={{ position: 'absolute', bottom: 'calc(100% + 4px)', left: 20, right: 20, maxHeight: 260, overflowY: 'auto', zIndex: 1500, borderRadius: 2, border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.08)', bgcolor: isDark ? '#1E1E2D' : '#ffffff', boxShadow: '0 -8px 32px rgba(0,0,0,0.2)' }}>
                                    <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>@ Clientes</Typography>
                                    </Box>
                                    {isSearchingClient && mentionResults.length === 0 ? (
                                        <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
                                    ) : mentionResults.length > 0 ? (
                                        <List dense sx={{ p: 0 }}>
                                            {mentionResults.map((client, idx) => (
                                                <ListItemButton key={idx} onClick={() => selectMention(client.name)} sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.03)', py: 1.2 }}>
                                                    <Avatar sx={{ width: 32, height: 32, mr: 1.5, fontSize: '0.9rem', bgcolor: 'primary.main', fontWeight: 700 }}>{client.name.charAt(0).toUpperCase()}</Avatar>
                                                    <ListItemText
                                                        primary={client.name}
                                                        secondary={client.phone || client.email}
                                                        primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
                                                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                                    />
                                                </ListItemButton>
                                            ))}
                                        </List>
                                    ) : (
                                        <Box sx={{ p: 2, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No se encontraron clientes</Typography></Box>
                                    )}
                                </Paper>
                            )}
                            <TextField
                                fullWidth
                                size="medium"
                                placeholder="Escribe tu mensaje... (@ clientes, # agendas)"
                                value={inputValue}
                                onChange={handleInputChange}
                                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                                multiline
                                maxRows={4}
                                autoComplete="off"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                InputProps={{
                                    endAdornment: (
                                        <>
                                            <IconButton size="small" onClick={handleVoiceRecord} disabled={isLoading} color={isListening ? 'error' : 'default'}>
                                                <MicIcon />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleSend()} disabled={!inputValue.trim() || isLoading} color="primary">
                                                <SendIcon />
                                            </IconButton>
                                        </>
                                    )
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Dialog>

            <Fade in={displayIsOpen && !isExpanded}>
                <Paper 
                    sx={{ 
                        position: 'fixed', 
                        bottom: { xs: 0, sm: 32 }, 
                        right: { xs: 0, sm: 32 }, 
                        width: { xs: '100%', sm: 400 }, 
                        height: { xs: '100dvh', sm: 600 },
                        zIndex: { xs: 99999, sm: 1100 },
                        borderRadius: { xs: 0, sm: 4 },
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        bgcolor: isDark ? 'rgba(20, 20, 30, 0.98)' : 'rgba(255, 255, 255, 0.98)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 20px 80px rgba(0,0,0,0.5)',
                        border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.05)'
                    }}
                >
                    {/* Header */}
                    <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => setShowHistory(true)}>
                            <Box sx={{ width: 40, height: 40, bgcolor: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <LynaAvatar size={35} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>Chateando con Lyna</Typography>
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>IA Asistente de Turn.ar</Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row">
                            <IconButton color="inherit" size="small" onClick={() => setShowHistory(!showHistory)} title="Historial">
                                <HistoryIcon />
                            </IconButton>
                            <IconButton color="inherit" size="small" onClick={loadStats} title="Ver resumen" disabled={isLoadingStats}>
                                {isLoadingStats ? <CircularProgress size={20} color="inherit" /> : <DashboardIcon />}
                            </IconButton>
                            <IconButton color="inherit" size="small" onClick={clearChat} title="Limpiar">
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                            <IconButton color="inherit" size="small" onClick={() => setIsExpanded(!isExpanded)} title={isExpanded ? 'Minimizar' : 'Expandir'}>
                                {isExpanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
                            </IconButton>
                            <IconButton color="inherit" size="small" onClick={() => {
                                if (onClose) onClose();
                                else setIsOpen(false);
                                setIsExpanded(false);
                            }}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                    </Box>

                    {/* History Panel */}
                    {showHistory ? (
                        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <span>💬</span> Historial de Conversaciones
                                </Typography>
                                <Button 
                                    size="small" 
                                    variant="contained" 
                                    color="primary"
                                    startIcon={<AddIcon fontSize="small" />}
                                    onClick={startNewConversation}
                                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
                                >
                                    Nueva Charla
                                </Button>
                            </Box>
                            <List dense sx={{ p: 0 }}>
                                {conversations.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 6, opacity: 0.5 }}>
                                        <HistoryIcon sx={{ fontSize: 48, mb: 1 }} />
                                        <Typography variant="body2">Sin conversaciones aún 🐾</Typography>
                                    </Box>
                                ) : (
                                    conversations.map((conv) => (
                                        <Paper
                                            key={conv.conversationId}
                                            elevation={0}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                fetchMessages(conv.conversationId);
                                            }}
                                            sx={{
                                                p: 1.5,
                                                mb: 1,
                                                borderRadius: '14px',
                                                cursor: 'pointer',
                                                bgcolor: conv.conversationId === conversationId ? (isDark ? 'rgba(155, 89, 182, 0.2)' : 'rgba(155, 89, 182, 0.08)') : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
                                                border: `1px solid ${conv.conversationId === conversationId ? 'rgba(155, 89, 182, 0.4)' : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)')}`,
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    borderColor: 'secondary.main',
                                                    transform: 'translateY(-1px)'
                                                }
                                            }}
                                        >
                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {conv.title || 'Conversación'}
                                                    </Typography>
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                                        <Chip label={`${conv.messageCount} msgs`} size="small" sx={{ height: 16, fontSize: '0.62rem', fontWeight: 700 }} />
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                            {formatTime(conv.lastMessageAt)}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                                <IconButton 
                                                    size="small" 
                                                    onClick={(e) => handleDeleteConversation(e, conv.conversationId)}
                                                    sx={{ p: 0.5, opacity: 0.4, '&:hover': { opacity: 1, color: '#ef4444' } }}
                                                    title="Eliminar conversación"
                                                >
                                                    <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                                                </IconButton>
                                            </Stack>
                                        </Paper>
                                    ))
                                )}
                            </List>
                        </Box>
                    ) : (
                        <>
                            {/* Messages Area */}
                            <Box 
                                ref={scrollRef}
                                sx={{ 
                                    flex: 1, 
                                    p: 2, 
                                    overflowY: 'auto', 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: 2,
                                    '&::-webkit-scrollbar': { width: '4px' },
                                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: '10px' }
                                }}
                            >
                                {messages.map((m, i) => (
                                    <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Box 
                                            sx={{ 
                                                maxWidth: '85%', 
                                                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                                                p: 1.5,
                                                borderRadius: m.role === 'user' ? '18px 18px 2px 18px' : '18px 18px 18px 2px',
                                                bgcolor: m.role === 'user' ? 'primary.main' : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
                                                color: m.role === 'user' ? 'white' : (isDark ? 'white' : 'inherit')
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: '0.875rem' }}>{m.content}</Typography>
                                        </Box>
                                        {(() => {
                                            const suggs = parseSuggestions(m.suggestions);
                                            if (suggs.length === 0 || m.role !== 'assistant') return null;
                                            return (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, ml: 1 }}>
                                                    {suggs.map((s, idx) => (
                                                        <Chip
                                                            key={idx}
                                                            label={s}
                                                            size="small"
                                                            onClick={() => handleSend(s)}
                                                            sx={{
                                                                bgcolor: isDark ? 'rgba(149, 117, 205, 0.18)' : 'rgba(99, 102, 241, 0.08)',
                                                                color: 'text.secondary',
                                                                fontSize: '0.75rem',
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    bgcolor: isDark ? 'rgba(149, 117, 205, 0.3)' : 'rgba(99, 102, 241, 0.15)'
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            );
                                        })()}
                                    </Box>
                                ))}
                                {isLoading && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                                        <CircularProgress size={16} />
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>Lyna está escribiendo...</Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Quick Questions */}
                            {messages.length === 1 && (
                                <Box sx={{ px: 2, pb: 1 }}>
                                    <Typography variant="caption" sx={{ opacity: 0.6, mb: 1, display: 'block', fontSize: '0.7rem' }}>
                                        Preguntas rápidas:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {quickQuestions.map((q, idx) => (
                                            <Chip
                                                key={idx}
                                                icon={q.icon}
                                                label={q.label}
                                                size="small"
                                                onClick={() => handleSend(q.query)}
                                                sx={{
                                                    bgcolor: isDark ? 'rgba(149, 117, 205, 0.15)' : 'rgba(99, 102, 241, 0.08)',
                                                    color: 'text.secondary',
                                                    fontSize: '0.75rem',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        bgcolor: isDark ? 'rgba(149, 117, 205, 0.3)' : 'rgba(99, 102, 241, 0.15)'
                                                    }
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}

                    {/* Board Mention Popover (#) */}
                    {showBoardMenu && (
                        <Paper
                            elevation={3}
                            sx={{
                                position: 'absolute',
                                bottom: 70,
                                left: 16,
                                right: 16,
                                maxHeight: 220,
                                overflowY: 'auto',
                                zIndex: 1200,
                                borderRadius: 2,
                                border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                                bgcolor: isDark ? '#1E1E2D' : '#ffffff'
                            }}
                        >
                            {isSearchingBoard && boardResults.length === 0 ? (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <CircularProgress size={20} />
                                </Box>
                            ) : boardResults.length > 0 ? (
                                <List dense sx={{ p: 0 }}>
                                    {boardResults.map((board, idx) => (
                                        <ListItemButton
                                            key={idx}
                                            onClick={() => selectBoardMention(board.name)}
                                            sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.03)' }}
                                        >
                                            <Avatar sx={{ width: 28, height: 28, mr: 1.5, fontSize: '0.85rem', bgcolor: 'secondary.main' }}>
                                                #
                                            </Avatar>
                                            <ListItemText
                                                primary={board.name}
                                                secondary={`${board.type?.replace('_', ' ')} · cap. ${board.capacity ?? '—'}`}
                                                primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                                                secondaryTypographyProps={{ fontSize: '0.7rem' }}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            ) : (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">No se encontraron agendas</Typography>
                                </Box>
                            )}
                        </Paper>
                    )}

                    {/* Client Mention Popover (@) */}
                    {showMentionMenu && (
                        <Paper 
                            elevation={3}
                            sx={{
                                position: 'absolute',
                                bottom: 70,
                                left: 16,
                                right: 16,
                                maxHeight: 200,
                                overflowY: 'auto',
                                zIndex: 1200,
                                borderRadius: 2,
                                border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
                                bgcolor: isDark ? '#1E1E2D' : '#ffffff'
                            }}
                        >
                            {isSearchingClient && mentionResults.length === 0 ? (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <CircularProgress size={20} />
                                </Box>
                            ) : mentionResults.length > 0 ? (
                                <List dense sx={{ p: 0 }}>
                                    {mentionResults.map((client, idx) => (
                                        <ListItemButton 
                                            key={idx} 
                                            onClick={() => selectMention(client.name)}
                                            sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.03)' }}
                                        >
                                            <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.8rem', bgcolor: 'primary.main' }}>
                                                {client.name.charAt(0).toUpperCase()}
                                            </Avatar>
                                            <ListItemText 
                                                primary={client.name} 
                                                secondary={client.phone || client.email} 
                                                primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }}
                                                secondaryTypographyProps={{ fontSize: '0.7rem' }}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            ) : (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">No se encontraron clientes</Typography>
                                </Box>
                            )}
                        </Paper>
                    )}

                    {/* Task Mention Popover (%) */}
                    {showTaskMenu && (
                        <Paper
                            elevation={3}
                            sx={{
                                position: 'absolute',
                                bottom: 70,
                                left: 16,
                                right: 16,
                                maxHeight: 220,
                                overflowY: 'auto',
                                zIndex: 1200,
                                borderRadius: 2,
                                border: isDark ? '1px solid rgba(99,102,241,0.3)' : '1px solid rgba(99,102,241,0.15)',
                                bgcolor: isDark ? '#1E1E2D' : '#ffffff'
                            }}
                        >
                            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366f1', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>% Tareas</Typography>
                            </Box>
                            {isSearchingTask && taskResults.length === 0 ? (
                                <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
                            ) : taskResults.length > 0 ? (
                                <List dense sx={{ p: 0 }}>
                                    {taskResults.map((task, idx) => {
                                        const pri = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                                        const sta = STATUS_CONFIG[task.status] || STATUS_CONFIG.todo;
                                        return (
                                            <ListItemButton key={idx} onClick={() => selectTaskMention(task.title)}
                                                sx={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.03)', py: 1 }}>
                                                <Avatar sx={{ width: 28, height: 28, mr: 1.5, fontSize: '0.75rem', bgcolor: pri.color, fontWeight: 700 }}>
                                                    {task.title.charAt(0).toUpperCase()}
                                                </Avatar>
                                                <ListItemText
                                                    primary={task.title}
                                                    secondary={`${sta.label} · ${pri.label}`}
                                                    primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
                                                    secondaryTypographyProps={{ fontSize: '0.7rem', color: sta.color }}
                                                />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            ) : (
                                <Box sx={{ p: 2, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No se encontraron tareas</Typography></Box>
                            )}
                        </Paper>
                     )}

                     {/* Input Area */}
                     <Box sx={{ p: 2, borderTop: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.05)' }}>
                        <TextField 
                            fullWidth
                            size="small"
                            placeholder="Escribe tu mensaje... (@ clientes, # agendas)"
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                            autoComplete="off"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 4 } }}
                            InputProps={{
                                endAdornment: (
                                    <>
                                        <IconButton 
                                            size="small" 
                                            onClick={handleVoiceRecord} 
                                            disabled={isLoading}
                                            color={isListening ? 'error' : 'default'}
                                        >
                                            <MicIcon />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleSend()} disabled={!inputValue.trim() || isLoading}>
                                            <SendIcon />
                                        </IconButton>
                                    </>
                                )
                            }}
                        />
                    </Box>
                </Paper>
            </Fade>

            {/* Dialog para confirmar creación de reserva */}
            <Dialog 
                open={createReservationDialog.open} 
                onClose={() => setCreateReservationDialog({ open: false, data: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>📅 Confirmar Reserva</DialogTitle>
                <DialogContent>
                    {createReservationDialog.data && (
                        <Box sx={{ pt: 1 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Lyna ha extraído los siguientes datos:
                            </Alert>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Cliente</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{createReservationDialog.data.clientName || 'No especificado'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Agenda</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{createReservationDialog.data.boardName || 'No especificado'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Inicio</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {createReservationDialog.data.startDate ? new Date(createReservationDialog.data.startDate).toLocaleString('es-ES') : 'No especificado'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Fin</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {createReservationDialog.data.endDate ? new Date(createReservationDialog.data.endDate).toLocaleString('es-ES') : 'Por defecto (1 hora)'}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => setCreateReservationDialog({ open: false, data: null })}>
                        Cancelar
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={() => handleCreateReservation(createReservationDialog.data)}
                        disabled={isLoading}
                    >
                        {isLoading ? <CircularProgress size={24} /> : 'Confirmar Reserva'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog para agregar comentario a una tarea */}
            <Dialog open={commentDialog.open} onClose={() => setCommentDialog({ open: false, taskId: null, taskTitle: '' })} maxWidth="sm" fullWidth>
                <DialogTitle>💬 Agregar Comentario</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Agregando comentario a: <strong>{commentDialog.taskTitle}</strong>
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Escribe tu comentario..."
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => { setCommentDialog({ open: false, taskId: null, taskTitle: '' }); setCommentText(''); }}>Cancelar</Button>
                    <Button variant="contained" onClick={handleAddComment} disabled={!commentText.trim()}>Guardar comentario</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LynaChat;
